import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { USER_ROLE_LABELS } from '@kn/shared';
import { useAuth } from '../auth/auth-context';
import { NAVIGATION } from '../navigation';
import { titreDeLaRoute } from '../hooks/titre-de-la-route';
import { SUFFIXE_TITRE, TitrePageContext } from '../hooks/useTitrePage';
import { VersionInstallee } from './VersionInstallee';

export function AppShell(): ReactElement {
  const { user, logout } = useAuth();
  const [deconnexionEnCours, setDeconnexionEnCours] = useState(false);

  /**
   * Le titre de l'onglet.
   *
   * Un seul endroit l'ecrit. La route donne le titre de base — aucun ecran ne
   * reste anonyme, meme ceux qu'on ajoutera — et une page qui en sait plus (le
   * libelle d'un article) l'annonce, auquel cas le sien l'emporte.
   */
  const { pathname } = useLocation();
  const [annonce, setAnnonce] = useState<string | null>(null);
  const annoncer = useCallback((titre: string | null) => setAnnonce(titre), []);

  useEffect(() => {
    const titre = annonce ?? titreDeLaRoute(pathname);
    document.title = titre ? `${titre} — ${SUFFIXE_TITRE}` : SUFFIXE_TITRE;
  }, [annonce, pathname]);

  const sections = NAVIGATION.map((section) => ({
    ...section,
    entries: section.entries.filter(
      (entree) => !entree.roles || (user && entree.roles.includes(user.role)),
    ),
  })).filter((section) => section.entries.length > 0);

  async function handleDeconnexion(): Promise<void> {
    setDeconnexionEnCours(true);
    try {
      await logout();
    } finally {
      setDeconnexionEnCours(false);
    }
  }

  return (
    <TitrePageContext.Provider value={annoncer}>
    <div className="app-shell">
      <div className="app-shell__marque">
        <strong>Journal douane / accises</strong>
        <span>Kuehne &amp; Nagel &times; Campari</span>
      </div>

      <header className="app-shell__entete">
        {user && (
          <div className="app-shell__identite">
            <strong>{user.displayName}</strong>
            <span>{USER_ROLE_LABELS[user.role]}</span>
          </div>
        )}
        <button
          type="button"
          className="bouton bouton--secondaire"
          onClick={() => void handleDeconnexion()}
          disabled={deconnexionEnCours}
        >
          Se deconnecter
        </button>
      </header>

      <nav className="app-shell__nav" aria-label="Navigation principale">
        {sections.map((section) => (
          <div key={section.label ?? 'principal'} className="nav-groupe">
            {section.label && <p className="nav-groupe__titre">{section.label}</p>}
            {section.entries.map((entree) =>
              entree.enabled ? (
                <NavLink
                  key={entree.path}
                  to={entree.path}
                  className={({ isActive }) =>
                    `nav-lien${isActive ? ' nav-lien--actif' : ''}` +
                    (entree.rattachee ? ' nav-lien--rattachee' : '')
                  }
                >
                  {entree.label}
                </NavLink>
              ) : (
                <span
                  key={entree.path}
                  className="nav-lien nav-lien--inactif"
                  aria-disabled="true"
                  title="Disponible dans une phase ulterieure"
                >
                  {entree.label}
                </span>
              ),
            )}
          </div>
        ))}
        <VersionInstallee />
      </nav>

      <main className="app-shell__contenu">
        <Outlet />
      </main>
    </div>
    </TitrePageContext.Provider>
  );
}
